<template>
  <div
    class="pricing"
    :class="{
      'pricing--large': large && !small,
      'pricing--small': small && !large,
      'pricing--row': row,
      'pricing--product-teaser': productTeaser,
      'pricing--product-variant': productVariant,
      'pricing--cart': cart,
      'pricing--search': search,
      'pricing--white': white,
      'pricing--with-friends': showFriendsPrice,
      'pricing--without-friends': hasNoBenefitAndPrice && !isWithFriendsPrice && (large || row),
    }"
  >
    <!-- Price tip badge -->
    <fn-tag
      v-if="isWithFriendsPrice || (hasFriendsBenefitAndPrice && (large || row))"
      class="p-price-tip"
      alert-detail
    >
      {{ $t('product.friends.badge') }}
    </fn-tag>

    <fn-tag
      v-if="showPriceTip"
      class="p-price-tip"
      price-tip
    >
      <span>{{ hasPriceTip }}</span>
    </fn-tag>

    <fn-tag
      v-else-if="hasDealBadge && dealBadgePositionTop"
      :class="pricingTypeClasses"
      data-hj-allow
      discount
    >
      <span>{{ hasDealBadge }}</span>
    </fn-tag>

    <!-- Former/savings badge -->
    <div class="p-savings">
      <fn-tag
        v-if="hasDealBadge && !dealBadgePositionTop"
        :class="pricingTypeClasses"
        data-hj-allow
        discount
      >
        <span>{{ hasDealBadge }}</span>
      </fn-tag>

      <template v-else-if="showSavingsPrice">
        <fn-tag
          v-if="showSavingsPriceBadge"
          :class="pricingTypeClasses"
          data-hj-allow
          discount
        >
          <span>{{ $t('product.savings') }}</span>
          <span>{{ formattedFormerPriceSavings }}</span>
        </fn-tag>

        <span
          v-else-if="showSavingsPrice || showRecommendedPrice"
          class="p-former-price"
        >
          <span
            v-if="showRecommendedPrice"
            class="p-recommended"
          >
            <span class="p-recommended-text">{{ $t('product.recommendedPrice') }}{{ ' ' }}</span>
          </span>
          <span
            class="p-former-price-value"
            :class="{
              'p-recommended-price-value': showRecommendedPrice,
            }"
            >{{ former || recommendedPrice }}</span
          >
        </span>
      </template>
    </div>

    <!-- Regular Price -->
    <div
      class="p-price"
      :class="pricingTypeClasses"
      data-hj-allow
    >
      <span
        v-if="showRegularText"
        class="p-regular-text"
      >
        {{ $t('product.regularPrice') }}:
      </span>
      <!-- For productTeaser -->
      <span
        v-if="!productTeaser && (showSavingsPrice || showRecommendedPrice)"
        class="p-former-price"
      >
        <span
          v-if="showRecommendedPrice"
          class="p-recommended"
        >
          <span class="p-recommended-text">{{ $t('product.recommendedPrice') }}{{ ' ' }}</span>
        </span>
        <span
          class="p-former-price-value"
          :class="{
            'p-recommended-price-value': showRecommendedPrice,
          }"
          >{{ former || recommendedPrice }}</span
        >
      </span>

      <span class="p-regular-price-value">
        {{ price }}
      </span>
    </div>
    <div
      v-if="perUnit"
      class="p-per-unit"
      :class="pricingTypeClasses"
      data-hj-allow
    >
      ({{ perUnit }})
    </div>

    <!-- Friends Price -->
    <div
      class="p-price p-friends-price"
      :class="pricingTypeClasses"
      data-hj-allow
    >
      <template v-if="isWithFriendsPrice || (hasFriendsBenefitAndPrice && (large || row))">
        {{ friendsPrice }}
      </template>
    </div>
    <div
      v-if="
        (isWithFriendsPrice && showFriendsPerUnit) ||
        (hasFriendsBenefitAndPrice && (large || row) && showFriendsPerUnit)
      "
      class="p-friends-per-unit"
      :class="pricingTypeClasses"
      data-hj-allow
    >
      ({{ friendsPricePerUnit }})
    </div>
    <fn-product-tax-and-shipping
      v-if="displayTaxInfo"
      class="p-tax-info"
      @change-tab="(tab) => $emit('changeTab', tab)"
    />
  </div>
</template>

<script>
export default defineComponent({
  props: {
    price: {
      required: true,
      type: String,
    },
    displayTaxInfo: {
      type: Boolean,
      detault: false,
    },
    former: {
      type: String,
      default: null,
    },
    formerPriceSavings: {
      type: Object,
      default: null,
    },
    recommendedPrice: {
      type: String,
      default: null,
    },
    perUnit: {
      type: String,
      default: null,
    },
    taxInfo: {
      type: Boolean,
    },
    large: {
      type: Boolean,
    },
    small: {
      type: Boolean,
    },
    productTeaser: {
      type: Boolean,
    },
    productVariant: {
      type: Boolean,
    },
    cart: {
      type: Boolean,
    },
    search: {
      type: Boolean,
    },
    white: {
      type: Boolean,
    },
    row: {
      type: Boolean,
    },
    friendsPrice: {
      type: String,
      default: '',
    },
    friendsPricePerUnit: {
      type: String,
      default: '',
    },
    badges: {
      type: Array,
      default: () => [],
    },
    hiddenBadges: {
      type: Array,
      default: () => [],
    },
    dealBadgePositionTop: {
      type: Boolean,
    },
  },
  computed: {
    ...mapState(useUserStore, {
      isAuthenticated: 'getIsAuthenticated',
      hasFriendsBenefit: 'getHasFriendsBenefit',
    }),

    formattedFormerPriceSavings() {
      return ` ${this.formerPriceSavings?.formattedValue}`;
    },

    showRegularText() {
      return (
        !this.showRecommendedPrice &&
        !this.showSavingsPrice &&
        !(!this.hasFriendsBenefitAndPrice && !this.hasNoBenefitAndPrice)
      );
    },

    showSavingsPrice() {
      // Hide savings for products with friends price, priority: friends > savings >= recommended
      return (this.former || this.formerPriceSavings?.value) && !this.showFriendsPrice;
    },

    showSavingsPriceBadge() {
      return this.showSavingsPrice && !this.productTeaser;
    },

    showRecommendedPrice() {
      // Hide recommended for products with savings price, priority: friends > savings >= recommended
      return this.recommendedPrice && !this.showFriendsPrice;
    },

    isFriendsPrice() {
      return (
        this.friendsPrice &&
        ((this.hasFriendsBenefit && this.cart) ||
          this.productTeaser ||
          this.productVariant ||
          this.large ||
          this.row ||
          this.search)
      );
    },

    showFriendsPrice() {
      // Hides savings + recommended price, priority: friends > savings >= recommended
      return this.hasFriendsBenefitAndPrice || (this.hasNoBenefitAndPrice && this.large) || this.isWithFriendsPrice;
    },

    showFriendsPerUnit() {
      return this.showFriendsPrice && this.friendsPricePerUnit;
    },

    hasFriendsBenefitAndPrice() {
      return this.isAuthenticated && this.isFriendsPrice && this.hasFriendsBenefit;
    },

    hasNoBenefitAndPrice() {
      return this.isFriendsPrice && !this.hasFriendsBenefit;
    },

    isWithFriendsPrice() {
      const isVariantValid = this.productTeaser || this.productVariant || this.cart || this.search;
      return this.isFriendsPrice && isVariantValid;
    },

    isRegularPrice() {
      return !this.isFriendsPrice || (this.isFriendsPrice && !this.isAuthenticated && !this.recommendedPrice);
    },

    pricingTypeClasses() {
      return {
        'p-with-friends': this.hasFriendsBenefitAndPrice,
        'p-without-friends': this.hasNoBenefitAndPrice,
        'p-regular-price': this.isRegularPrice,
        'p-with-savings': this.showSavingsPrice,
        'p-neutral-deal-price': this.showNeutralDealPrice,
        'p-with-recommended': this.showRecommendedPrice,
      };
    },

    hasPriceTip() {
      return this.badges?.find((badge) => badge.type === 'priceTip')?.name || null;
    },

    hasDealBadge() {
      if (this.hiddenBadges?.includes('deal')) {
        return;
      }

      return this.badges?.find((badge) => badge.type === 'deal')?.name;
    },

    showNeutralDealPrice() {
      return this.badges?.find((badge) => badge.type === 'deal')?.name && !this.former;
    },

    showPriceTip() {
      return (
        !this.isWithFriendsPrice &&
        !this.hasFriendsBenefitAndPrice &&
        !this.showSavingsPrice &&
        this.hasPriceTip !== null
      );
    },
  },
});
</script>

<style lang="scss" scoped>
/**
 * The pricing component is quite complex and deserves a little introduction:
 *
 * We currently use CSS grid with `grid-template-areas` to align the different pricing variants. This was once
 * easy to handle due to its flexibility, but recent changes to our layouts blew up complexity to the point where
 * changing anything can easily break something else.
 *
 * The component can be used as a `subgrid` to align multiple pricing components based a single shared CSS grid.
 */

@mixin grid-template-areas-default() {
  grid-template-columns: 1fr;
  grid-template-areas:
    'tag'
    'price'
    'per-unit'
    'friends-price'
    'friends-per-unit'
    'thirty-days-best-price'
    'tax-info';
}

@mixin grid-template-areas-friends() {
  grid-template-columns: 1fr 1fr;
  grid-template-areas:
    '. tag' 'price friends-price' 'per-unit friends-per-unit' '. thirty-days-best-price'
    'tax-info tax-info';

  .p-price.p-friends-price,
  .p-friends-per-unit {
    justify-self: end;
  }

  .p-price:not(.p-friends-price),
  .p-per-unit {
    justify-self: start;
  }
}

.pricing {
  // Note: Pricing component is still used without any variant by e.g. ImageWithHotspots.
  // For maintainability reasons we might create a separate variant and move its grid styles
  // there, then we can keep the base pricing styles lean and tidy.
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-areas:
    '. tag'
    'price price'
    'per-unit per-unit'
    'thirty-days-best-price thirty-days-best-price'
    'tax-info tax-info';
  justify-items: end;
  align-items: center;
  row-gap: math.div($pad, 8);

  // Define and provide overview of available grid-areas
  @include grid-area-classes(
    (
      'p-price-tip': 'tag',
      'p-savings': 'tag',
      'p-price': 'price',
      'p-per-unit': 'per-unit',
      'p-friends-price': 'friends-price',
      'p-friends-per-unit': 'friends-per-unit',
      'p-tax-info': 'tax-info',
    )
  );

  // First-level items - classes below must exist in grid-area mapping
  .tag--discount {
    margin-bottom: math.div($pad, 8);
  }

  .p-savings {
    font-size: map-get($font-sizes, 's');
    line-height: map-get($font-line-heights, 'xxxs');
    color: $color-general-text-promotion-discount-default;

    // Inline tag spacing
    // Inline span in fn-tag content
    :deep(.tag) {
      &:not(:first-child) {
        margin-left: math.div($pad, 4);
      }

      span {
        font-weight: map-get($font-weights, 'bold');
      }
    }
  }

  .p-price {
    font-weight: map-get($font-weights, 'bold');
    align-self: end;

    &.p-with-friends {
      font-size: map-get($body, 'medium');
      line-height: 1.4;
      justify-self: start;
    }

    &.p-without-friends {
      justify-self: end;
    }

    &.p-friends-price {
      &.p-with-friends {
        font-size: map-get($body, 'large');
        color: $color-general-text-promotion-discount-default;
        line-height: 1.4;
      }
    }

    .p-regular-text,
    .p-recommended-text {
      font-size: map-get($label, 'small');
    }

    .p-regular-text {
      margin-right: math.div($pad, 4);
      line-height: 1.2;

      @include bp(m) {
        font-size: map-get($body, 'medium');
        line-height: 1.4;
      }
    }

    &.p-with-savings.p-with-recommended {
      .p-former-price {
        .p-former-price-value {
          &.p-recommended-price-value {
            text-decoration: none;
          }
        }
      }
    }
  }

  .p-per-unit,
  .p-friends-per-unit {
    align-self: start;
    font-size: map-get($font-sizes, 'm');
    font-weight: map-get($font-weights, 'medium');
    color: $color-general-text-neutral-lower;
    line-height: 1.2;
  }

  // Second-level items - classes below are not in the grid-area mapping
  .p-recommended {
    font-size: map-get($label, 'small');
    color: $color-general-text-neutral-lower;
  }

  .p-former-price {
    margin-right: math.div($pad, 4);
    font-size: map-get($font-sizes, 'm');
    font-weight: map-get($font-weights, 'medium');
    color: $color-general-text-neutral-lower;

    .p-former-price-value {
      text-decoration: line-through;

      &.p-recommended-price-value {
        text-decoration: none;
      }
    }
  }

  // Variants
  /*
   * Note: This is *not* the plain opposite of "without-friends", as both will be false if not logged in
   * Used mostly everywhere? Afaik this is more like a modifiers of other variants than a new variant.
   * Note: These styles should be defined before all other styles to less less specificity headaches.
   */
  &.pricing--with-friends {
    grid-template-areas:
      '. tag'
      'price friends-price'
      'per-unit friends-per-unit'
      'thirty-days-best-price thirty-days-best-price'
      'tax-info tax-info';
  }

  /*
   * Note: This is *not* the plain opposite of "with-friends", as both will be false if not logged in
   * Used mostly everywhere? Afaik this is more like a modifiers of other variants than a new variant.
   * Note: These styles should be defined before all other styles to less less specificity headaches.
   */
  &.pricing--without-friends {
    grid-template-areas:
      '. .'
      'price price'
      'per-unit per-unit'
      'thirty-days-best-price thirty-days-best-price'
      'tax-info tax-info';
  }

  /*
   * White text
   */
  &.pricing--white {
    .p-price,
    .p-per-unit,
    .p-savings {
      color: $color-general-text-inverse-default;
    }
  }

  /*
   * Small text
   * Used by e.g. ProductSingleTeaser
   */
  &.pricing--small {
    .p-price {
      font-size: map-get($font-sizes, 'xs');
    }
  }

  /*
   * Used by e.g. AddToCartForm/PricingBox
   */
  &.pricing--large {
    &.pricing--with-friends,
    &.pricing--without-friends {
      .p-friends-per-unit {
        margin-bottom: $pad * 0.75;
      }
    }

    .tag--discount {
      margin-bottom: math.div($pad, 4);
    }

    .p-price {
      display: flex;
      align-items: center;

      &.p-friends-price {
        justify-self: end;
        color: $color-general-text-promotion-discount-default;
        font-size: map-get($headline, 'small');
        line-height: 1.2;

        @include bp(m) {
          font-size: map-get($headline, 'medium');
        }

        &.p-with-friends,
        &.p-with-savings {
          color: $color-general-text-promotion-discount-default;
        }
      }

      &.p-with-savings,
      &.p-with-recommended,
      &.p-neutral-deal-price {
        .p-regular-text {
          color: $color-general-text-neutral-lower;
        }

        .p-regular-price-value {
          color: $color-general-text-promotion-discount-default;
          font-size: map-get($label, 'xlarge');

          @include bp(m) {
            font-size: map-get($body, 'medium');
          }
        }
      }

      &.p-with-savings,
      &.p-neutral-deal-price {
        color: $color-general-text-promotion-discount-default;
      }

      &.p-with-recommended {
        &.p-with-savings,
        &.p-neutral-deal-price {
          &.p-regular-price {
            .p-regular-price-value {
              color: $color-general-text-promotion-discount-default;
            }
          }
        }
      }

      &.p-with-recommended {
        &:not(.p-savings) {
          .p-regular-price-value {
            color: $color-general-text-neutral-default;
          }
        }
      }

      .p-regular-price-value,
      &.p-without-friends,
      &.p-without-friends.p-regular-price {
        .p-regular-price-value {
          font-size: map-get($label, 'xlarge');
          line-height: 1.2;

          @include bp(m) {
            font-size: map-get($body, 'medium');
            line-height: 1.4;
          }
        }
      }

      &.p-regular-price {
        .p-regular-price-value {
          font-size: map-get($label, 'xlarge');

          @include bp(m) {
            font-size: map-get($headline, 'medium');
            line-height: 1.2;
          }
        }
      }

      &.p-with-friends {
        .p-regular-price-value {
          font-size: map-get($label, 'xlarge');

          @include bp(m) {
            font-size: map-get($body, 'medium');
          }
        }
      }
    }

    .p-per-unit {
      &.p-with-friends {
        align-self: start;
        justify-self: start;
      }
    }

    .p-per-unit,
    .p-friends-per-unit {
      font-size: map-get($font-sizes, 'm');
    }
  }

  /*
   * Used by e.g. PDP/ProductVariantSlider
   */
  &.pricing--product-variant {
    @include subgrid-inherit();
    @include grid-template-areas-default();
    justify-items: start;

    .p-price {
      display: flex;
      grid-gap: math.div($pad, 4);
      align-items: baseline;
      flex-direction: row-reverse;
      line-height: 1.2;
      letter-spacing: -0.02em;
      justify-self: start;

      &.p-regular-text {
        display: inline;
      }

      &.p-regular-price {
        font-size: map-get($font-sizes, 'lxl');

        .p-regular-price-value {
          line-height: 1.4;
        }
      }

      &.p-regular-price,
      &.p-with-friends {
        &:not(.p-friends-price) {
          display: inline;
        }
      }

      // Increase specificity to prevent !important
      &.p-with-savings,
      &.p-with-friends,
      &.p-without-friends,
      &.p-neutral-deal-price {
        .p-regular-text,
        .p-regular-price-value {
          margin: 0;
          font-size: map-get($font-sizes, 'm');
          color: $color-general-text-promotion-discount-default;
        }
      }

      &.p-with-savings,
      &.p-neutral-deal-price {
        .p-regular-price-value {
          font-size: map-get($body, 'medium');
          color: $color-general-text-promotion-discount-default;
        }
      }
    }

    &.pricing--without-friends,
    &.pricing--with-friends {
      grid-template-areas:
        'tag'
        'friends-price'
        'friends-per-unit'
        'price'
        'per-unit'
        'thirty-days-best-price'
        'tax-info';

      .p-price {
        flex-direction: row;
        color: $color-general-text-promotion-discount-default;
        font-size: map-get($font-sizes, 'lxl');

        .p-regular-text,
        .p-regular-price-value {
          color: $color-general-text-neutral-default;
          font-size: map-get($font-sizes, 'm');
          line-height: 1.2;
        }

        &:not(.p-friends-price) {
          margin-top: $pad - (math.div($pad, 4));
        }
      }
    }
  }

  /*
   * Used by e.g. WishlistItem
   */
  &.pricing--row {
    @include grid-template-areas-default();

    &.pricing--with-friends,
    &.pricing--without-friends {
      @include grid-template-areas-friends();
    }
  }

  /*
   * Used by e.g. PLP
   */
  &.pricing--product-teaser {
    grid-template-columns: auto;
    grid-template-areas:
      'tag'
      'price'
      'per-unit'
      'thirty-days-best-price'
      'tax-info';
    justify-items: start;
    align-items: center;
    margin-top: $pad * 0.75;

    .p-price {
      justify-self: start;

      .p-regular-text,
      .p-regular-price-value {
        font-size: map-get($body, 'large');
        line-height: 1.4;
      }
    }

    // Note: Do not use "without-friends" here, it is *not* the plain opposite of "with-friends"
    // since both will be false if a user is not authenticated
    &:not(&.pricing--with-friends) {
      .p-price {
        display: flex;
        grid-gap: math.div($pad, 4);
        align-items: baseline;
        flex-direction: row-reverse;

        &.p-with-savings,
        &.p-neutral-deal-price {
          flex-direction: column;

          .p-regular-price-value {
            color: $color-general-text-promotion-discount-default;
          }
        }
      }
    }

    &.pricing--with-friends {
      grid-template-areas:
        'tag'
        'friends-price'
        'friends-per-unit'
        'price'
        'per-unit'
        'thirty-days-best-price'
        'tax-info';

      .p-price {
        font-size: map-get($font-sizes, 'l');
        color: $color-general-text-neutral-default;

        &.p-friends-price {
          font-size: map-get($font-sizes, 'xl') !important;
          color: $color-general-text-promotion-discount-default;
        }

        .p-regular-text,
        .p-regular-price-value {
          font-size: map-get($font-sizes, 'm');
        }

        &:not(.p-friends-price).p-with-friends,
        &:not(.p-friends-price).p-without-friends.p-regular-price {
          margin-top: $pad * 0.75;
        }
      }
    }

    .p-with-recommended {
      .p-regular-text,
      .p-regular-price-value,
      &.p-without-friends,
      &.p-without-friends.p-regular-price {
        .p-regular-price-value {
          font-size: map-get($font-sizes, 'm');

          @include bp(m) {
            font-size: map-get($font-sizes, 'lxl');
          }
        }
      }
    }

    .p-per-unit {
      line-height: 1.2;
      margin-top: 0;
    }
  }

  /*
   * Used by e.g. ProductSingleTeaser
   */
  &.pricing--cart {
    @include subgrid-inherit();
    @include grid-template-areas-default();

    .p-price {
      .p-regular-price-value {
        font-size: map-get($body, 'large');
      }

      &.p-with-friends {
        .p-regular-text,
        .p-regular-price-value {
          font-size: map-get($body, 'small');
        }
      }
    }

    .p-regular-price {
      &.p-with-savings,
      &.p-neutral-deal-price {
        .p-regular-price-value {
          color: $color-general-text-promotion-discount-default;
          line-height: 1.4;
        }
      }
    }

    .p-recommended {
      font-weight: map-get($font-weights, 'normal');
    }

    &.pricing--with-friends,
    &.pricing--without-friends {
      @include grid-template-areas-friends();
    }
  }

  /*
   * Used by e.g. FormSearchProduct
   */
  &.pricing--search {
    line-height: 1.5;
    @include grid-template-areas-default();

    .p-regular-price-value {
      font-size: map-get($font-sizes, 'l');
    }

    .tag {
      display: none;
    }

    .p-price {
      .p-former-price,
      &.p-with-friends:not(.p-friends-price),
      &.p-without-friends:not(.p-friends-price) {
        display: none;
      }

      &.p-with-savings,
      &.p-friends-price,
      &.p-neutral-deal-price {
        color: $color-general-text-promotion-discount-default;
        font-size: map-get($font-sizes, 'l');
        justify-self: end;
      }
    }

    .p-per-unit {
      &.p-with-friends:not(.p-friends-per-unit),
      &.p-without-friends:not(.p-friends-per-unit) {
        display: none;
      }
    }

    .p-per-unit,
    .p-friends-per-unit {
      font-size: map-get($font-sizes, 'm');
      color: $color-general-text-neutral-default;
    }
  }
}
</style>
